import { shoppingListClient } from './client';
import { ConnectError, Code } from '@connectrpc/connect';
import { create } from '@bufbuild/protobuf';
import {
  CreateShoppingListRequestSchema,
  GetShoppingListRequestSchema,
  ListShoppingListsRequestSchema,
  UpdateShoppingListRequestSchema,
  DeleteShoppingListRequestSchema,
  AddShoppingListItemRequestSchema,
  UpdateShoppingListItemRequestSchema,
  RemoveShoppingListItemRequestSchema,
  CheckShoppingListItemRequestSchema,
  PairReceiptToShoppingListRequestSchema,
  UnpairReceiptFromShoppingListRequestSchema,
  GetShoppingListByReceiptRequestSchema,
  ShoppingListSortField,
  ShoppingListSortOrder,
  ShoppingListStatus,
  type ShoppingListItemInput,
  type CreateShoppingListResponse,
  type GetShoppingListResponse,
  type ListShoppingListsResponse,
  type UpdateShoppingListResponse,
  type DeleteShoppingListResponse,
  type AddShoppingListItemResponse,
  type UpdateShoppingListItemResponse,
  type RemoveShoppingListItemResponse,
  type CheckShoppingListItemResponse,
  type PairReceiptToShoppingListResponse,
  type UnpairReceiptFromShoppingListResponse,
  type GetShoppingListByReceiptResponse,
} from '../../generated/shopping/v1/shopping_pb';

export { ShoppingListSortField, ShoppingListSortOrder, ShoppingListStatus };

export interface CreateShoppingListParams {
  name?: string;
  store?: string;
  date?: string;
  items?: ShoppingListItemInput[];
  sourceReceiptId?: string;
}

export interface GetShoppingListParams {
  listId: string;
}

export interface ListShoppingListsParams {
  pageSize?: number;
  page?: number;
  sortBy?: ShoppingListSortField;
  sortOrder?: ShoppingListSortOrder;
  search?: string;
  statusFilter?: ShoppingListStatus;
  hasPairedReceipt?: boolean;
}

export interface UpdateShoppingListParams {
  listId: string;
  name?: string;
  store?: string;
  date?: string;
  status?: ShoppingListStatus;
}

export interface PairReceiptToShoppingListParams {
  listId: string;
  receiptId: string;
}

export interface UnpairReceiptFromShoppingListParams {
  listId: string;
}

export interface GetShoppingListByReceiptParams {
  receiptId: string;
}

export interface DeleteShoppingListParams {
  listId: string;
}

export interface AddShoppingListItemParams {
  listId: string;
  item: ShoppingListItemInput;
}

export interface UpdateShoppingListItemParams {
  listId: string;
  itemId: string;
  description?: string;
  customDescription?: string;
  quantity?: number;
  unitPrice?: number;
  price?: number;
  category?: string;
}

export interface RemoveShoppingListItemParams {
  listId: string;
  itemId: string;
}

export interface CheckShoppingListItemParams {
  listId: string;
  itemId: string;
  isChecked: boolean;
}

export const createShoppingList = async (
  params: CreateShoppingListParams
): Promise<CreateShoppingListResponse> => {
  try {
    const request = create(CreateShoppingListRequestSchema, {
      name: params.name,
      store: params.store,
      date: params.date,
      items: params.items ?? [],
      sourceReceiptId: params.sourceReceiptId,
    });

    const response = await shoppingListClient.createShoppingList(request);
    return response;
  } catch (error) {
    console.error('Create shopping list failed:', error);
    throw handleShoppingListError(error, 'create shopping list');
  }
};

export const getShoppingList = async (
  params: GetShoppingListParams
): Promise<GetShoppingListResponse> => {
  try {
    const request = create(GetShoppingListRequestSchema, {
      listId: params.listId,
    });

    const response = await shoppingListClient.getShoppingList(request);
    return response;
  } catch (error) {
    console.error('Get shopping list failed:', error);
    throw handleShoppingListError(error, 'get shopping list');
  }
};

export const listShoppingLists = async (
  params?: ListShoppingListsParams
): Promise<ListShoppingListsResponse> => {
  try {
    const request = create(ListShoppingListsRequestSchema, {
      pageSize: params?.pageSize,
      page: params?.page,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      search: params?.search,
      statusFilter: params?.statusFilter,
      hasPairedReceipt: params?.hasPairedReceipt,
    });

    const response = await shoppingListClient.listShoppingLists(request);
    return response;
  } catch (error) {
    console.error('List shopping lists failed:', error);
    throw handleShoppingListError(error, 'list shopping lists');
  }
};

export const updateShoppingList = async (
  params: UpdateShoppingListParams
): Promise<UpdateShoppingListResponse> => {
  try {
    const request = create(UpdateShoppingListRequestSchema, {
      listId: params.listId,
      name: params.name,
      store: params.store,
      date: params.date,
      status: params.status,
    });

    const response = await shoppingListClient.updateShoppingList(request);
    return response;
  } catch (error) {
    console.error('Update shopping list failed:', error);
    throw handleShoppingListError(error, 'update shopping list');
  }
};

export const deleteShoppingList = async (
  params: DeleteShoppingListParams
): Promise<DeleteShoppingListResponse> => {
  try {
    const request = create(DeleteShoppingListRequestSchema, {
      listId: params.listId,
    });

    const response = await shoppingListClient.deleteShoppingList(request);
    return response;
  } catch (error) {
    console.error('Delete shopping list failed:', error);
    throw handleShoppingListError(error, 'delete shopping list');
  }
};

export const addShoppingListItem = async (
  params: AddShoppingListItemParams
): Promise<AddShoppingListItemResponse> => {
  try {
    const request = create(AddShoppingListItemRequestSchema, {
      listId: params.listId,
      item: params.item,
    });

    const response = await shoppingListClient.addShoppingListItem(request);
    return response;
  } catch (error) {
    console.error('Add shopping list item failed:', error);
    throw handleShoppingListError(error, 'add shopping list item');
  }
};

export const updateShoppingListItem = async (
  params: UpdateShoppingListItemParams
): Promise<UpdateShoppingListItemResponse> => {
  try {
    const request = create(UpdateShoppingListItemRequestSchema, {
      listId: params.listId,
      itemId: params.itemId,
      description: params.description,
      customDescription: params.customDescription,
      quantity: params.quantity,
      unitPrice: params.unitPrice,
      price: params.price,
      category: params.category,
    });

    const response = await shoppingListClient.updateShoppingListItem(request);
    return response;
  } catch (error) {
    console.error('Update shopping list item failed:', error);
    throw handleShoppingListError(error, 'update shopping list item');
  }
};

export const removeShoppingListItem = async (
  params: RemoveShoppingListItemParams
): Promise<RemoveShoppingListItemResponse> => {
  try {
    const request = create(RemoveShoppingListItemRequestSchema, {
      listId: params.listId,
      itemId: params.itemId,
    });

    const response = await shoppingListClient.removeShoppingListItem(request);
    return response;
  } catch (error) {
    console.error('Remove shopping list item failed:', error);
    throw handleShoppingListError(error, 'remove shopping list item');
  }
};

export const checkShoppingListItem = async (
  params: CheckShoppingListItemParams
): Promise<CheckShoppingListItemResponse> => {
  try {
    const request = create(CheckShoppingListItemRequestSchema, {
      listId: params.listId,
      itemId: params.itemId,
      isChecked: params.isChecked,
    });

    const response = await shoppingListClient.checkShoppingListItem(request);
    return response;
  } catch (error) {
    console.error('Check shopping list item failed:', error);
    throw handleShoppingListError(error, 'check shopping list item');
  }
};

export const pairReceiptToShoppingList = async (
  params: PairReceiptToShoppingListParams
): Promise<PairReceiptToShoppingListResponse> => {
  try {
    const request = create(PairReceiptToShoppingListRequestSchema, {
      listId: params.listId,
      receiptId: params.receiptId,
    });

    const response = await shoppingListClient.pairReceiptToShoppingList(request);
    return response;
  } catch (error) {
    console.error('Pair receipt to shopping list failed:', error);
    throw handleShoppingListError(error, 'pair receipt to shopping list');
  }
};

export const unpairReceiptFromShoppingList = async (
  params: UnpairReceiptFromShoppingListParams
): Promise<UnpairReceiptFromShoppingListResponse> => {
  try {
    const request = create(UnpairReceiptFromShoppingListRequestSchema, {
      listId: params.listId,
    });

    const response = await shoppingListClient.unpairReceiptFromShoppingList(request);
    return response;
  } catch (error) {
    console.error('Unpair receipt from shopping list failed:', error);
    throw handleShoppingListError(error, 'unpair receipt from shopping list');
  }
};

export const getShoppingListByReceipt = async (
  params: GetShoppingListByReceiptParams
): Promise<GetShoppingListByReceiptResponse> => {
  try {
    const request = create(GetShoppingListByReceiptRequestSchema, {
      receiptId: params.receiptId,
    });

    const response = await shoppingListClient.getShoppingListByReceipt(request);
    return response;
  } catch (error) {
    console.error('Get shopping list by receipt failed:', error);
    throw handleShoppingListError(error, 'get shopping list by receipt');
  }
};

function handleShoppingListError(error: unknown, operation: string): Error {
  if (error instanceof ConnectError) {
    if (error.code === Code.Unauthenticated) {
      return new Error('Session expired. Please log in again.');
    }
    if (error.code === Code.PermissionDenied) {
      return new Error('Access denied. You do not have permission to perform this action.');
    }
    if (error.code === Code.InvalidArgument) {
      return new Error(`Invalid request: ${error.message || 'Please check your input'}`);
    }

    const message = error.message || '';
    if (
      message.includes('Load failed') ||
      message.includes('Failed to fetch') ||
      message.includes('NetworkError')
    ) {
      return new Error(
        `Failed to ${operation}: Network error. Please check your connection and try again.`
      );
    }

    if (error.message) {
      return new Error(`Failed to ${operation}: ${error.message}`);
    }
  }

  return new Error(
    `Failed to ${operation}: ${error instanceof Error ? error.message : 'Unknown error'}`
  );
}
